import type { Breed, Listing, MediaAsset, Pet, Post, Tag, User } from 'shared/@generated/models';
import type { Paginator } from 'shared/@types';
import type { MediaImageProfile } from 'shared/library/media-image-versions';

export type ComparisonCardBreed = Pick<Breed, 'id' | 'name' | 'slug'> & {
  avatar: Pick<MediaAsset, 'url'> | null;
};

export type BreedDetail = Omit<Breed, 'breed_questions'> & {
  breed_questions: Pick<Breed['breed_questions'][number], 'question' | 'answer'>[];
};

export function compactBreedForDetail(breed: Breed): BreedDetail {
  return {
    ...breed,
    breed_questions: breed.breed_questions.map(({ question, answer }) => ({ question, answer })),
  };
}

export function compactBreedForComparison(breed: Breed): ComparisonCardBreed {
  return {
    id: breed.id,
    name: breed.name,
    slug: breed.slug,
    avatar: breed.avatar ? { url: breed.avatar.url } : null,
  };
}

function compactPaginator<TInput, TOutput>(
  paginator: Paginator<TInput>,
  compactItem: (item: TInput) => TOutput,
): Paginator<TOutput> {
  return nullifyUndefined({
    ...paginator,
    data: paginator.data.map(compactItem),
  });
}

function nullifyUndefined<T>(value: T): T {
  if (value === undefined) {
    return null as T;
  }

  if (Array.isArray(value)) {
    return value.map(nullifyUndefined) as T;
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, nullifyUndefined(item)]),
    ) as T;
  }

  return value;
}

function compactMediaAsset(
  asset: MediaAsset | null | undefined,
  imageProfiles?: MediaImageProfile[],
): MediaAsset | null {
  if (!asset) {
    return null;
  }

  const compact: MediaAsset & { video_fallback_url?: string | null } = {
    id: asset.id,
    uuid: asset.uuid,
    type: asset.type,
    description: asset.description,
    url: asset.url,
    ...(asset.video_url ? { video_url: asset.video_url } : {}),
  } as MediaAsset;

  if (imageProfiles) {
    if (asset.focus_area !== undefined) compact.focus_area = asset.focus_area;
    if (asset.source_format !== undefined) compact.source_format = asset.source_format;

    const videoFallbackUrl = (asset as MediaAsset & { video_fallback_url?: string | null })
      .video_fallback_url;
    if (videoFallbackUrl !== undefined) compact.video_fallback_url = videoFallbackUrl;

    const imageVersions = asset.image_versions;
    if (imageVersions !== undefined) {
      compact.image_versions = Array.isArray(imageVersions)
        ? []
        : Object.fromEntries(
            imageProfiles
              .filter((profile) => imageVersions[profile] !== undefined)
              .map((profile) => [profile, imageVersions[profile]]),
          );
    }
  }

  return nullifyUndefined(compact);
}

function compactMediaAssets(
  assets: MediaAsset[] | null | undefined,
  imageProfiles?: MediaImageProfile[],
): MediaAsset[] {
  return (assets ?? [])
    .map((asset) => compactMediaAsset(asset, imageProfiles))
    .filter((asset): asset is MediaAsset => asset !== null);
}

export function compactBreedForCard(breed: Breed): Breed {
  return nullifyUndefined({
    id: breed.id,
    name: breed.name,
    slug: breed.slug,
    animal: breed.animal,
    breed_type: breed.breed_type,
    active_pets_count: breed.active_pets_count,
    avatar: compactMediaAsset(breed.avatar),
    media_assets: compactMediaAssets(breed.media_assets?.filter((asset) => asset?.url).slice(0, 1)),
    owned_media_assets: compactMediaAssets(
      breed.owned_media_assets?.filter((asset) => asset?.url).slice(0, 1),
    ),
  } as Breed);
}

const LISTING_CARD_MEDIA_ASSET_LIMIT = 5;

type PetWithPedigreeLink = Pet & {
  pedigree_link?: string | null;
};

function compactListingCardPets(pets: Pet[] | null | undefined): Pet[] {
  return (pets ?? []).map((pet) => {
    const petWithPedigreeLink = pet as PetWithPedigreeLink;

    return nullifyUndefined({
      id: pet.id,
      listing_id: pet.listing_id,
      role: pet.role,
      registration_id: pet.registration_id,
      pedigree_link: petWithPedigreeLink.pedigree_link,
    } as PetWithPedigreeLink);
  });
}

export function compactTagForLink(tag: Tag): Tag {
  return nullifyUndefined({
    id: tag.id,
    slug: tag.slug,
    name: tag.name,
    description: tag.description,
    featured_order: tag.featured_order,
    posts_count: tag.posts_count,
  } as Tag);
}

export function compactTagsForLinks(tags: Tag[] | null | undefined): Tag[] {
  return (tags ?? []).map(compactTagForLink);
}

export function compactTagForBlogTopic(tag: Tag): Tag {
  return nullifyUndefined({
    ...compactTagForLink(tag),
    posts: compactPostCardList(tag.posts).slice(0, 5),
  } as Tag);
}

export function compactTagsForBlogTopics(tags: Tag[] | null | undefined): Tag[] {
  return (tags ?? []).map(compactTagForBlogTopic);
}

function compactListingCardUser(user: User): User {
  return nullifyUndefined({
    id: user.id,
    uuid: user.uuid,
    display_name: user.display_name,
    first_name: user.first_name,
    last_name: user.last_name,
    city: user.city,
    country: user.country,
    is_breeder: user.is_breeder,
    breeder_id: user.breeder_id,
    avatar: compactMediaAsset(user.avatar, ['avatar']),
    breeder: user.breeder
      ? ({
          id: user.breeder.id,
          slug: user.breeder.slug,
          name: user.breeder.name,
          avatar: compactMediaAsset(user.breeder.avatar, ['avatar']),
        } as User['breeder'])
      : user.breeder,
  } as User);
}

export function compactListingForCard(listing: Listing): Listing {
  const listingWithGenderCounts = listing as Listing & {
    males_count?: number;
    females_count?: number;
  };

  return nullifyUndefined({
    id: listing.id,
    slug: listing.slug,
    animal: listing.animal ?? null,
    birth_date: listing.birth_date,
    published_at: listing.published_at,
    deleted_at: listing.deleted_at,
    breed_id: listing.breed_id,
    breed_name: listing.breed_name,
    breed_type: listing.breed_type,
    pet_age: listing.pet_age,
    title: listing.title,
    price: listing.price,
    currency: listing.currency,
    short_description: listing.short_description,
    description: listing.description,
    is_insured: listing.is_insured,
    males_count: listingWithGenderCounts.males_count,
    females_count: listingWithGenderCounts.females_count,
    pets: compactListingCardPets(listing.pets),
    avatar: compactMediaAsset(listing.avatar, ['card']),
    media_assets: compactMediaAssets(listing.media_assets, ['card']).slice(
      0,
      LISTING_CARD_MEDIA_ASSET_LIMIT,
    ),
    user: compactListingCardUser(listing.user),
  } as unknown as Listing);
}

export function compactListingCardPaginator(paginator: Paginator<Listing>): Paginator<Listing> {
  return compactPaginator(paginator, compactListingForCard);
}

export function compactListingCardList(listings: Listing[]): Listing[] {
  return listings.map(compactListingForCard);
}

export function compactPostForCard(post: Post): Post {
  return nullifyUndefined({
    id: post.id,
    slug: post.slug,
    title: post.title,
    summary: post.summary,
    published_at: post.published_at,
    featured_order: post.featured_order,
    reading_time: post.reading_time,
    tags: compactTagsForLinks(post.tags).slice(0, 3),
    avatar: compactMediaAsset(post.avatar),
    media_assets: compactMediaAssets(post.media_assets).slice(0, 1),
    owned_media_assets: compactMediaAssets(post.owned_media_assets).slice(0, 1),
  } as Post);
}

export function compactPostCardPaginator(paginator: Paginator<Post>): Paginator<Post> {
  return compactPaginator(paginator, compactPostForCard);
}

export function compactPostCardList(posts: Post[] | null | undefined): Post[] {
  return (posts ?? []).map(compactPostForCard);
}
